'use client';

import React, { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import PostCard, { PostCardProps } from '@/components/PostCard';
import PostCardSkeleton from '@/components/skeletons/PostCard';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { API_BASE as API_BASE_RAW } from '@/lib/apiBase';
import { authHeaders } from '@/lib/api';
import PostsHelpfulLinks from '@/components/posts/PostsHelpfulLinks';
import { Plus, Briefcase, Camera, Users, Sparkles, MapPin, ChevronLeft, Inbox } from 'lucide-react';

const API_BASE = API_BASE_RAW.replace(/\/+$/, '');

// Per-bucket icon + tagline + accent. Used for the eyebrow row + glow tint.
// Tagline omits the location since the breadcrumb + the parent index already
// communicate it; the title carries the category specificity.
const TYPE_THEME: Record<string, {
  label: string;
  tagline: string;
  Icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  glow: string;
}> = {
  hiring: {
    label: 'Now hiring',
    tagline: 'Producers and studios offering paid work.',
    Icon: Briefcase,
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-500',
    glow: 'bg-amber-500/10',
  },
  looking: {
    label: 'Now hiring',
    tagline: 'Producers and crew booking talent for paid work.',
    Icon: Briefcase,
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-500',
    glow: 'bg-amber-500/10',
  },
  connect: {
    label: 'Collaborate',
    tagline: 'Members looking for talent partners.',
    Icon: Users,
    iconBg: 'bg-pink-500/10',
    iconColor: 'text-pink-500',
    glow: 'bg-fuchsia-500/10',
  },
  services: {
    label: 'Services',
    tagline: 'Crew, support, and creator services.',
    Icon: Sparkles,
    iconBg: 'bg-violet-500/10',
    iconColor: 'text-violet-400',
    glow: 'bg-violet-500/10',
  },
};

// Map API response to PostCardProps with correct field names
function mapPost(p: any): PostCardProps {
  return {
    ...p,
    unsafe: (p.isUnsafeContent ?? p.IsUnsafeContent ?? p.unsafe) !== false,
    // /api/post returns `lastModified` (and sometimes `datePosted`); PostCard expects `createdAt` for the time-ago meta line.
    createdAt: p.createdAt ?? p.CreatedAt ?? p.datePosted ?? p.DatePosted ?? p.lastModified ?? p.LastModified ?? null,
  };
}

export default function CategoryClientPage({
  initialPosts,
  initialRelated,
  initialCats,
  editorial = null,
}: {
  initialPosts?: PostCardProps[] | null;
  initialRelated?: PostCardProps[] | null;
  initialCats?: { slug: string; name: string; type?: string }[] | null;
  editorial?: { label: string; intro: string; type: string } | null;
}) {
  const rp = useParams() as Partial<{ location: string; category: string }>;
  const location = String(rp?.location ?? '');
  const category = String(rp?.category ?? '');

  const [posts, setPosts] = useState<PostCardProps[] | null>(initialPosts ? initialPosts.map(mapPost) : null);
  const [related, setRelated] = useState<PostCardProps[] | null>(initialRelated ? initialRelated.map(mapPost) : null);
  const [cats, setCats] = useState<{ slug: string; name: string; type?: string }[] | null>(initialCats ?? null);
  const [error, setError] = useState(false);
  const [loadingPrimary, setLoadingPrimary] = useState<boolean>(
    initialPosts == null || (Array.isArray(initialPosts) && initialPosts.length === 0)
  );

  const placeName = String(location || '')
    .replace(/-/g, ' ')
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  useEffect(() => {
    async function load() {
      setError(false);
      const reqHeaders = await authHeaders();
      const isAuthed = Boolean((reqHeaders as any).Authorization);
      const hasInitialPosts = initialPosts != null && Array.isArray(initialPosts) && initialPosts.length > 0;
      if (!hasInitialPosts) setPosts(null);
      setLoadingPrimary(true);
      if (initialRelated == null) setRelated(null);

      try {
        let nextPosts: any[] | null = null;
        if (!hasInitialPosts || isAuthed) {
          const res = await fetch(
            `${API_BASE}/api/post?locationSlug=${encodeURIComponent(location)}&categorySlug=${encodeURIComponent(category)}&limit=20`,
            { cache: 'no-store', headers: reqHeaders }
          );
          if (res.ok) {
            const data: any[] = await res.json();
            const getId = (p: any) => Number(p?.postId ?? p?.PostId ?? p?.id ?? p?.Id ?? 0);
            nextPosts = Array.isArray(data) ? data.filter((p, i, arr) => {
              const id = getId(p);
              if (!id) return true;
              return arr.findIndex(q => getId(q) === id) === i;
            }) : [];
            setPosts(nextPosts.map(mapPost));
          } else {
            nextPosts = [];
            if (!hasInitialPosts) setPosts([]);
          }
        }

        const needRelated = (initialRelated == null) || ((!hasInitialPosts || isAuthed) && (Array.isArray(nextPosts) && nextPosts.length === 0));
        if (needRelated) {
          try {
            const relatedRes = await fetch(
              `${API_BASE}/api/post/related?locationSlug=${encodeURIComponent(location)}&categorySlug=${encodeURIComponent(category)}&limit=10`,
              { cache: 'no-store', headers: reqHeaders }
            );
            if (relatedRes.ok) {
              const rel: any[] = await relatedRes.json();
              const getId2 = (p: any) => Number(p?.postId ?? p?.PostId ?? p?.id ?? p?.Id ?? 0);
              const dedupRel = Array.isArray(rel) ? rel.filter((p, i, arr) => {
                const id = getId2(p);
                if (!id) return true;
                return arr.findIndex(q => getId2(q) === id) === i;
              }) : [];
              setRelated(dedupRel.map(mapPost));
              if (dedupRel.length === 0) {
                const nat = await fetch(`${API_BASE}/api/posts/feed?limit=10`, { cache: 'no-store', headers: reqHeaders });
                if (nat.ok) {
                  const natJson = await nat.json();
                  const arr = Array.isArray((natJson as any)?.items) ? (natJson as any).items : (Array.isArray(natJson) ? (natJson as any) : []);
                  const dedupNat = Array.isArray(arr) ? arr.filter((p: any, i: number, a: any[]) => {
                    const id = Number(p?.postId ?? p?.PostId ?? p?.id ?? p?.Id ?? 0);
                    return id ? a.findIndex(q => Number(q?.postId ?? q?.PostId ?? q?.id ?? q?.Id ?? 0) === id) === i : true;
                  }) : [];
                  setRelated(dedupNat.map(mapPost));
                }
              }
            } else {
              setRelated([]);
            }
          } catch { setRelated([]); }
        }

        if (initialCats == null) {
          try {
            const catsRes = await fetch(`${API_BASE}/api/categories?metro=${encodeURIComponent(location)}`, { cache: 'no-store', headers: reqHeaders });
            setCats(catsRes.ok ? await catsRes.json() : []);
          } catch { setCats([]); }
        }
      } catch {
        setError(true);
      } finally {
        setLoadingPrimary(false);
      }
    }
    load();
  }, [location, category]);

  const hasPrimary = (posts?.length ?? 0) > 0;
  const showRelated = !hasPrimary && related && related.length > 0;

  const currentCat = Array.isArray(cats) ? cats.find(c => c.slug === category) : undefined;
  const catType = (currentCat?.type || '').toLowerCase();
  const theme = TYPE_THEME[catType] || TYPE_THEME.hiring;
  const HeroIcon = theme.Icon;
  const displayName = editorial?.label || currentCat?.name || category.replace(/-/g, ' ').replace(/\b\w/g, s => s.toUpperCase());

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
      {/* Breadcrumb */}
      <nav className="mb-3 text-sm text-zinc-500 dark:text-zinc-400" aria-label="Breadcrumb">
        <Link href="/posts" className="hover:underline">Posts</Link>
        <span className="mx-2 text-zinc-300 dark:text-zinc-700">/</span>
        <Link href={`/posts/${location}`} className="hover:underline">{placeName}</Link>
        <span className="mx-2 text-zinc-300 dark:text-zinc-700">/</span>
        {/* Human name, not the raw slug uppercased (CASTING-FEMALE-TALENT-FM). */}
        <span className="font-semibold text-zinc-900 dark:text-zinc-100">{displayName}</span>
      </nav>

      {/* Hero card — mirrors the posts index hero */}
      <section className="relative mb-6 overflow-hidden rounded-3xl border border-zinc-200/60 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-8">
        <div
          aria-hidden
          className={cn('pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full blur-3xl', theme.glow)}
        />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2">
              <span className={cn('inline-flex h-7 w-7 items-center justify-center rounded-lg', theme.iconBg)}>
                <HeroIcon className={cn('h-4 w-4', theme.iconColor)} />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {theme.label}
              </span>
            </div>
            <h1 className="font-serif text-[clamp(2rem,5.5vw,3.25rem)] font-semibold leading-[1.05] tracking-tight text-zinc-900 dark:text-zinc-50">
              {displayName}
            </h1>
            <p className="mt-3 max-w-lg text-balance text-base text-zinc-600 dark:text-zinc-300 sm:text-lg">
              {theme.tagline}
            </p>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
            <Button asChild type="button" variant="outline" className="gap-2">
              <Link href={`/posts/${location}`}>
                <ChevronLeft className="h-4 w-4" aria-hidden />
                <span>All categories</span>
              </Link>
            </Button>
            <Button asChild variant="primary" className="gap-2">
              <Link href={`/posts/create/${location}/${category}`}>
                <Plus className="h-4 w-4" aria-hidden />
                <span>Create a post</span>
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Durable category editorial — gives the page unique, indexable body
          content (and feeds the index gate for job-intent categories). */}
      {editorial?.intro && (
        <section className="mb-6 rounded-3xl border border-zinc-200/60 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-8">
          <p className="max-w-3xl text-base leading-relaxed text-zinc-600 dark:text-zinc-300">
            {editorial.intro}
          </p>
        </section>
      )}

      {error ? (
        <div className="rounded-2xl border border-zinc-200/60 bg-white p-8 text-center text-sm text-zinc-600 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-300">
          Error loading posts.
        </div>
      ) : posts === null ? (
        <div className="grid gap-4 grid-cols-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <PostCardSkeleton key={i} variant="light" showMeta />
          ))}
        </div>
      ) : hasPrimary ? (
        <div className="grid gap-4 grid-cols-1">
          {/* No ribbon on these cards: every card shares this page's category,
              which the H1 already states (PostCard's own prop doc says hide it). */}
          {posts.map(post => (
            <PostCard key={post.postId} {...post} showMeta variant="light" showCategoryRibbon={false} />
          ))}
        </div>
      ) : (
        <section className="space-y-6">
          {/* Empty state — same card chrome as the rest of the page */}
          <div className="rounded-3xl border border-zinc-200/60 bg-white p-10 text-center shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100 dark:bg-zinc-900">
              <Inbox className="h-6 w-6 text-zinc-400" aria-hidden />
            </div>
            <h2 className="text-xl font-serif font-semibold text-zinc-900 dark:text-zinc-50">
              No {displayName} posts in {placeName} yet
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-zinc-600 dark:text-zinc-300">
              Be the first to post in this area.
            </p>
            <div className="mt-5 flex justify-center">
              <Button asChild variant="primary" className="gap-2">
                <Link href={`/posts/create/${location}/${category}`}>
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>Create a post</span>
                </Link>
              </Button>
            </div>
          </div>

          {Array.isArray(cats) && cats.length > 0 && (
            <div className="rounded-3xl border border-zinc-200/60 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-8">
              <h3 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                Explore other categories in {placeName}
              </h3>
              <ul className="flex flex-wrap gap-2">
                {cats.filter(c => c.slug !== category).slice(0, 12).map(c => (
                  <li key={c.slug}>
                    <Link
                      href={`/posts/${location}/${c.slug}`}
                      className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    >
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {showRelated && (
            <div>
              <h3 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                Nearby posts
              </h3>
              <div className="grid gap-4 grid-cols-1">
                {related!.map(p => (
                  <PostCard key={`rel-${p.postId}`} {...p} showMeta variant="light" showCategoryRibbon />
                ))}
              </div>
            </div>
          )}

          <div className="rounded-3xl border border-zinc-200/60 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-8">
            <h3 className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              <MapPin className="h-3.5 w-3.5" aria-hidden />
              <span>Try nearby or popular cities</span>
            </h3>
            <ul className="flex flex-wrap gap-2 text-sm">
              {['los-angeles','new-york','miami','chicago','houston','dallas'].map(mslug => (
                <li key={mslug}>
                  <Link
                    href={`/posts/${mslug}`}
                    className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-zinc-700 hover:bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  >
                    {mslug.replace(/-/g,' ').replace(/\b\w/g, s => s.toUpperCase())}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <PostsHelpfulLinks />
        </section>
      )}
    </div>
  );
}
